/**
 * Página de Treinamentos e Comunicação — CRUD com novo, editar, deletar
 */

import { useState } from 'react';
import { Plus, GraduationCap, Users, Pencil, Trash2, MoreHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { CrudDialog } from '@/components/shared/CrudDialog';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

interface TrainingLocal {
  id: string; title: string; mandatory: boolean; status: string; participants: number; completed: number; due: string;
}

const initialTrainings: TrainingLocal[] = [
  { id: '1', title: 'NR-35 — Trabalho em Altura', mandatory: true, status: 'em_andamento', participants: 15, completed: 10, due: '2025-03-15' },
  { id: '2', title: 'NR-12 — Segurança em Máquinas', mandatory: true, status: 'planejado', participants: 20, completed: 0, due: '2025-04-01' },
  { id: '3', title: 'CIPA — Comissão Interna', mandatory: false, status: 'concluido', participants: 8, completed: 8, due: '2025-02-20' },
];

export default function TrainingsPage() {
  const [trainings, setTrainings] = useState<TrainingLocal[]>(initialTrainings);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<TrainingLocal> | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const openNew = () => { setEditing({ title: '', mandatory: false, status: 'planejado', participants: 0, completed: 0, due: '' }); setDialogOpen(true); };
  const openEdit = (t: TrainingLocal) => { setEditing({ ...t }); setDialogOpen(true); };
  const openDelete = (id: string) => { setSelectedId(id); setConfirmOpen(true); };

  const handleSave = () => {
    if (!editing?.title) { toast.error('Título é obrigatório'); return; }
    if (editing.id) {
      setTrainings(prev => prev.map(t => t.id === editing.id ? { ...t, ...editing } as TrainingLocal : t));
      toast.success('Treinamento atualizado');
    } else {
      setTrainings(prev => [...prev, { ...editing, id: `train-${Date.now()}` } as TrainingLocal]);
      toast.success('Treinamento criado');
    }
  };

  const handleDelete = () => { if (selectedId) { setTrainings(prev => prev.filter(t => t.id !== selectedId)); toast.success('Treinamento removido'); } };

  return (
    <div>
      <PageHeader title="Treinamentos" description="Treinamentos obrigatórios e campanhas internas">
        <Button onClick={openNew}><Plus className="mr-2 h-4 w-4" /> Novo Treinamento</Button>
      </PageHeader>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {trainings.map(t => (
          <Card key={t.id} className="hover:border-primary/30 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><GraduationCap className="h-4 w-4 text-primary" />{t.title}</CardTitle>
                <div className="flex items-center gap-1">
                  <StatusBadge status={t.status} />
                  <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(t)}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openDelete(t.id)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Excluir</DropdownMenuItem>
                  </DropdownMenuContent></DropdownMenu>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {t.mandatory && <Badge variant="outline" className="text-[10px] text-warning border-warning/30">Obrigatório</Badge>}
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Users className="h-3 w-3" /> {t.completed}/{t.participants} concluídos</div>
              <div className="w-full bg-muted rounded-full h-1.5"><div className="h-1.5 rounded-full bg-primary transition-all" style={{ width: `${t.participants > 0 ? (t.completed / t.participants) * 100 : 0}%` }} /></div>
              <p className="text-xs text-muted-foreground">Prazo: {t.due ? new Date(t.due).toLocaleDateString('pt-BR') : '—'}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <CrudDialog open={dialogOpen} onOpenChange={setDialogOpen} title={editing?.id ? 'Editar Treinamento' : 'Novo Treinamento'} onSave={handleSave}>
        <div className="space-y-3">
          <div><Label>Título</Label><Input value={editing?.title || ''} onChange={e => setEditing(prev => prev ? { ...prev, title: e.target.value } : prev)} /></div>
          <div><Label>Prazo</Label><Input type="date" value={editing?.due || ''} onChange={e => setEditing(prev => prev ? { ...prev, due: e.target.value } : prev)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Participantes</Label><Input type="number" min={0} value={editing?.participants || 0} onChange={e => setEditing(prev => prev ? { ...prev, participants: parseInt(e.target.value) || 0 } : prev)} /></div>
            <div><Label>Concluídos</Label><Input type="number" min={0} value={editing?.completed || 0} onChange={e => setEditing(prev => prev ? { ...prev, completed: parseInt(e.target.value) || 0 } : prev)} /></div>
          </div>
          <div><Label>Status</Label>
            <Select value={editing?.status || 'planejado'} onValueChange={v => setEditing(prev => prev ? { ...prev, status: v } : prev)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="planejado">Planejado</SelectItem><SelectItem value="em_andamento">Em Andamento</SelectItem><SelectItem value="concluido">Concluído</SelectItem></SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={editing?.mandatory || false} onCheckedChange={v => setEditing(prev => prev ? { ...prev, mandatory: !!v } : prev)} />
            Obrigatório
          </label>
        </div>
      </CrudDialog>

      <ConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} title="Excluir Treinamento" description="Tem certeza? Esta ação não pode ser desfeita." onConfirm={handleDelete} confirmLabel="Excluir" />
    </div>
  );
}
